import { useEffect, useMemo, useState } from 'react';

import AppModal from '../components/AppModal';
import api from '../services/api';
import { esAdministrador } from '../utils/usuarioActual';

function estadoInicial() {
  return {
    Id: null,
    Usuario: '',
    Nombre: '',
    Rol: 'Operador',
    Password: ''
  };
}

function UsuariosPage() {
  const usuarioEsAdministrador = esAdministrador();

  const [usuarios, setUsuarios] = useState([]);
  const [form, setForm] = useState(estadoInicial());
  const [editando, setEditando] = useState(false);
  const [mensaje, setMensaje] = useState('');
  const [errores, setErrores] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [filtro, setFiltro] = useState('');
  const [usuarioEstatusModal, setUsuarioEstatusModal] = useState(null);

  useEffect(() => {
    if (usuarioEsAdministrador) {
      cargarUsuarios();
    } else {
      setCargando(false);
    }
  }, [usuarioEsAdministrador]);

  async function cargarUsuarios() {
    try {
      setCargando(true);
      setMensaje('');

      const response = await api.get('/usuarios');

      setUsuarios(response.data || []);
    } catch (error) {
      setMensaje(
        error.response?.data?.mensaje ||
        'No fue posible cargar usuarios.'
      );
    } finally {
      setCargando(false);
    }
  }

  function handleChange(e) {
    const { name, value } = e.target;

    setForm(prev => ({
      ...prev,
      [name]: value
    }));
  }

  function limpiarFormulario() {
    setForm(estadoInicial());
    setEditando(false);
    setErrores([]);
    setMensaje('');
  }

  function validar() {
    const pendientes = [];

    if (!form.Usuario.trim()) {
      pendientes.push('Usuario es obligatorio.');
    }

    if (!form.Nombre.trim()) {
      pendientes.push('Nombre es obligatorio.');
    }

    if (!form.Rol) {
      pendientes.push('Rol es obligatorio.');
    }

    if (!editando && !form.Password.trim()) {
      pendientes.push('Contraseña es obligatoria para usuarios nuevos.');
    }

    return pendientes;
  }

  async function guardarUsuario(e) {
    e.preventDefault();

    setMensaje('');
    setErrores([]);

    const pendientes = validar();

    if (pendientes.length > 0) {
      setErrores(pendientes);
      return;
    }

    const payload = {
      Usuario: form.Usuario.trim(),
      Nombre: form.Nombre.trim(),
      Rol: form.Rol,
      Password: form.Password.trim()
    };

    try {
      setGuardando(true);

      if (editando) {
        await api.put(`/usuarios/${form.Id}`, payload);
        setMensaje('Usuario actualizado correctamente.');
      } else {
        await api.post('/usuarios', payload);
        setMensaje('Usuario creado correctamente.');
      }

      setForm(estadoInicial());
      setEditando(false);
      await cargarUsuarios();
    } catch (error) {
      setMensaje(
        error.response?.data?.mensaje ||
        'No fue posible guardar el usuario.'
      );
    } finally {
      setGuardando(false);
    }
  }

  function editarUsuario(usuario) {
    setForm({
      Id: usuario.Id,
      Usuario: usuario.Usuario || '',
      Nombre: usuario.Nombre || '',
      Rol: usuario.Rol || 'Operador',
      Password: ''
    });

    setEditando(true);
    setMensaje('');
    setErrores([]);

    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }

  function cambiarEstatus(usuario) {
    setUsuarioEstatusModal(usuario);
  }

  async function confirmarCambioEstatus() {
    const usuario = usuarioEstatusModal;

    if (!usuario) return;

    setUsuarioEstatusModal(null);

    const nuevoActivo = !usuario.Activo;

    try {
      setMensaje('');

      await api.patch(`/usuarios/${usuario.Id}/estatus`, {
        Activo: nuevoActivo
      });

      setMensaje(
        nuevoActivo
          ? 'Usuario activado correctamente.'
          : 'Usuario desactivado correctamente.'
      );

      await cargarUsuarios();
    } catch (error) {
      setMensaje(
        error.response?.data?.mensaje ||
        'No fue posible cambiar el estatus del usuario.'
      );
    }
  }

  const usuariosFiltrados = useMemo(() => {
    const texto = filtro.trim().toLowerCase();

    if (!texto) return usuarios;

    return usuarios.filter((usuario) => (
      String(usuario.Usuario || '').toLowerCase().includes(texto) ||
      String(usuario.Nombre || '').toLowerCase().includes(texto) ||
      String(usuario.Rol || '').toLowerCase().includes(texto)
    ));
  }, [usuarios, filtro]);

  if (!usuarioEsAdministrador) {
    return (
      <div className="card-box">
        <h5>Acceso restringido</h5>

        <p className="text-muted mb-0">
          La administración de usuarios solo está disponible para usuarios Administrador.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="card-box mb-3">
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div>
            <h5 className="mb-1">
              {editando ? 'Editar usuario' : 'Nuevo usuario'}
            </h5>

            <div className="text-muted" style={{ fontSize: '14px' }}>
              Alta, edición y baja lógica de accesos al sistema.
            </div>
          </div>
        </div>

        {mensaje && (
          <div className="alert alert-info">
            {mensaje}
          </div>
        )}

        {errores.length > 0 && (
          <div className="alert alert-warning">
            <div className="fw-bold mb-2">
              Revisa los campos:
            </div>

            <ul className="mb-0">
              {errores.map((error, index) => (
                <li key={index}>
                  {error}
                </li>
              ))}
            </ul>
          </div>
        )}

        <form onSubmit={guardarUsuario}>
          <div className="row g-3 align-items-end">
            <div className="col-md-3">
              <label className="form-label">
                Usuario <span className="text-danger">*</span>
              </label>

              <input
                className="form-control"
                name="Usuario"
                value={form.Usuario}
                onChange={handleChange}
                autoComplete="off"
                placeholder="Ej. operador1"
              />
            </div>

            <div className="col-md-4">
              <label className="form-label">
                Nombre <span className="text-danger">*</span>
              </label>

              <input
                className="form-control"
                name="Nombre"
                value={form.Nombre}
                onChange={handleChange}
                autoComplete="off"
                placeholder="Nombre completo"
              />
            </div>

            <div className="col-md-2">
              <label className="form-label">
                Rol <span className="text-danger">*</span>
              </label>

              <select
                className="form-select"
                name="Rol"
                value={form.Rol}
                onChange={handleChange}
              >
                <option value="Operador">Operador</option>
                <option value="Administrador">Administrador</option>
              </select>
            </div>

            <div className="col-md-3">
              <label className="form-label">
                {editando ? 'Nueva contraseña' : 'Contraseña'}
                {!editando && (
                  <span className="text-danger"> *</span>
                )}
              </label>

              <input
                className="form-control"
                name="Password"
                type="password"
                value={form.Password}
                onChange={handleChange}
                autoComplete="new-password"
                placeholder={editando ? 'Dejar en blanco para conservar' : ''}
              />
            </div>

            <div className="col-md-12">
              <div className="d-flex gap-2 justify-content-end">
                {editando && (
                  <button
                    className="btn btn-outline-secondary"
                    type="button"
                    onClick={limpiarFormulario}
                  >
                    Cancelar edición
                  </button>
                )}

                <button
                  className="btn btn-primary"
                  type="submit"
                  disabled={guardando}
                >
                  {guardando
                    ? 'Guardando...'
                    : editando
                      ? 'Guardar cambios'
                      : 'Crear usuario'}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>

      <div className="card-box">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h5 className="mb-1">Usuarios registrados</h5>
            <div className="text-muted" style={{ fontSize: '14px' }}>
              No se eliminan usuarios físicamente; se activan o desactivan.
            </div>
          </div>

          <div style={{ width: '280px' }}>
            <input
              className="form-control"
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
              placeholder="Buscar usuario"
            />
          </div>
        </div>

        {cargando ? (
          <div className="text-muted">
            Cargando usuarios...
          </div>
        ) : (
          <div className="table-wrap">
            <table className="table table-sm table-hover align-middle usuarios-table">
              <thead>
                <tr>
                  <th>Usuario</th>
                  <th>Nombre</th>
                  <th>Rol</th>
                  <th className="text-center">Estatus</th>
                  <th className="usuarios-col-acciones">Acciones</th>
                </tr>
              </thead>

              <tbody>
                {usuariosFiltrados.map((usuario) => (
                  <tr key={usuario.Id}>
                    <td>
                      <strong>{usuario.Usuario}</strong>
                    </td>

                    <td>{usuario.Nombre}</td>

                    <td>{usuario.Rol}</td>

                    <td className="text-center">
                      {usuario.Activo ? (
                        <span className="badge bg-success">Activo</span>
                      ) : (
                        <span className="badge bg-secondary">Inactivo</span>
                      )}
                    </td>

                    <td className="usuarios-col-acciones">
                      <div className="usuarios-acciones">
                        <button
                          className="btn btn-sm btn-outline-primary"
                          type="button"
                          onClick={() => editarUsuario(usuario)}
                        >
                          Editar
                        </button>

                        <button
                          className={
                            usuario.Activo
                              ? 'btn btn-sm btn-outline-danger'
                              : 'btn btn-sm btn-outline-success'
                          }
                          type="button"
                          onClick={() => cambiarEstatus(usuario)}
                        >
                          {usuario.Activo ? 'Desactivar' : 'Activar'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}

                {usuariosFiltrados.length === 0 && (
                  <tr>
                    <td
                      colSpan="5"
                      className="text-center text-muted py-4"
                    >
                      No hay usuarios registrados.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <AppModal
        abierto={Boolean(usuarioEstatusModal)}
        titulo="Sistema Recibos"
        mensaje={
          usuarioEstatusModal
            ? `¿Deseas ${usuarioEstatusModal.Activo ? 'desactivar' : 'activar'} el usuario ${usuarioEstatusModal.Usuario}?`
            : ''
        }
        variante={usuarioEstatusModal?.Activo ? 'danger' : 'warning'}
        textoCancelar="Cancelar"
        textoAceptar={usuarioEstatusModal?.Activo ? 'Desactivar' : 'Activar'}
        onCancelar={() => setUsuarioEstatusModal(null)}
        onAceptar={confirmarCambioEstatus}
      />
    </div>
  );
}

export default UsuariosPage;
